// components/BatchTable.tsx
import React from 'react'
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Typography,
  CircularProgress
} from '@mui/material'
import { Batch } from '../../../interfaces/Batch'
import { formatExpirationDate } from '../../../utils/dateHelper'
import { getStyleRowFromDate } from '../../../utils/colors'

interface BatchTableProps {
  batches: Batch[]
  showBatchNumber?: boolean
  showExpirationDate?: boolean
  showQuantity?: boolean
  modalType?: 'batch' | 'expiration' | 'both'
  isLoading?: boolean
  emptyText?: string
  onBatchClick: (batch: Batch) => void
  getBackgroundStyle?: (
    expirationDate: string | undefined,
    skuProductOrigin: number
  ) => React.CSSProperties | undefined
  skuProductOriginDays?: number
}

export const BatchTable: React.FC<BatchTableProps> = ({
  batches,
  showBatchNumber = true,
  showExpirationDate = false,
  showQuantity = false,
  isLoading = false,
  emptyText = 'No hay coincidencias',
  onBatchClick,
  getBackgroundStyle,
  skuProductOriginDays = 0
}) => {
  if (isLoading) {
    return (
      <Typography sx={(theme) => ({ p: theme.spacing(2), textAlign: 'center' })}>
        <CircularProgress size={24} />
      </Typography>
    )
  }

  if (batches.length === 0) {
    return (
      <Typography variant='body2' sx={(theme) => ({ p: theme.spacing(2), textAlign: 'center' })}>
        {emptyText}
      </Typography>
    )
  }

  const renderTableHeaders = () => {
    const headers = []

    if (showBatchNumber) {
      headers.push(
        <TableCell
          key='batch'
          sx={(theme) => ({
            backgroundColor: theme.palette.grey[200],
            fontWeight: 600,
            textAlign: 'center'
          })}
        >
          Lote
        </TableCell>
      )
    }

    if (showExpirationDate) {
      headers.push(
        <TableCell
          key='expiration'
          sx={(theme) => ({
            backgroundColor: theme.palette.grey[200],
            fontWeight: 600,
            textAlign: 'center'
          })}
        >
          Fecha de Vencimiento
        </TableCell>
      )
    }

    if (showQuantity) {
      headers.push(
        <TableCell
          key='quantity'
          sx={(theme) => ({
            backgroundColor: theme.palette.grey[200],
            fontWeight: 600,
            textAlign: 'center'
          })}
        >
          Cantidad
        </TableCell>
      )
    }

    return headers
  }

  const useCorrectStyleFunction = getBackgroundStyle || getStyleRowFromDate

  const renderTableCells = (batch: Batch) => {
    const cells = []

    if (showBatchNumber) {
      cells.push(
        <TableCell
          key='batch-value'
          style={{
            ...useCorrectStyleFunction(
              batch.expirationDate as string,
              skuProductOriginDays
            ),
            fontSize: '0.8rem'
          }}
          sx={(theme) => ({
            p: theme.spacing(1),
            textAlign: 'center'
          })}
        >
          <Typography>{batch.batchNumber}</Typography>
        </TableCell>
      )
    }

    if (showExpirationDate) {
      cells.push(
        <TableCell
          key='expiration-value'
          style={{
            ...useCorrectStyleFunction(
              batch.expirationDate as string,
              skuProductOriginDays
            ),
            fontSize: '0.8rem'
          }}
          sx={(theme) => ({
            p: theme.spacing(1),
            textAlign: 'center'
          })}
        >
          {batch.expirationDate ? (
            <Typography>
              {formatExpirationDate(batch.expirationDate, 'dd-MM-yyyy')}
            </Typography>
          ) : (
            'N/A'
          )}
        </TableCell>
      )
    }

    if (showQuantity) {
      cells.push(
        <TableCell
          key='quantity-value'
          style={{
            ...useCorrectStyleFunction(
              batch.expirationDate as string,
              skuProductOriginDays
            ),
            fontSize: '0.8rem'
          }}
          sx={(theme) => ({
            p: theme.spacing(1),
            textAlign: 'center'
          })}
        >
          {batch.quantity !== undefined ? (
            <Typography>{batch.quantity}</Typography>
          ) : (
            'N/A'
          )}
        </TableCell>
      )
    }

    return cells
  }

  return (
    <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
      <Table size='small' stickyHeader>
        <TableHead>
          <TableRow>{renderTableHeaders()}</TableRow>
        </TableHead>
        <TableBody>
          {batches.map((batch, idx) => (
            <TableRow
              key={batch.batchNumber + batch.expirationDate}
              onClick={() => onBatchClick(batch)}
              sx={(theme) => ({
                cursor: 'pointer',
                backgroundColor:
                  idx % 2 === 0 ? theme.palette.grey[50] : undefined,
                '&:hover': {
                  backgroundColor: theme.palette.grey[200]
                }
              })}
            >
              {renderTableCells(batch)}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
